import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, GitMerge, Split, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const euro = (n) => (n ? (Number(n) || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }) : '—');
const tid = (chiave) => chiave.replace(/\|/g, '--');
const STATO = { unisci: ['Uniti', 'bg-emerald-100 text-emerald-800'], separati: ['Separati', 'bg-gray-100 text-gray-700'] };

const VinoBox = ({ v, etichetta }) => (
  <div className="flex-1 min-w-0 border rounded-lg p-3 bg-white">
    <p className="text-[10px] uppercase tracking-wide text-gray-400">{etichetta}</p>
    <p className="text-sm font-semibold text-gray-900">{v.nome}</p>
    <p className="text-xs text-gray-500">{[v.cantina, v.annata].filter(Boolean).join(' · ')}</p>
    <p className="text-xs text-gray-500 mt-1 break-words">Fattura: <span className="text-gray-700">{v.nome_originale}</span></p>
    <p className="text-xs text-gray-500">{v.fornitore}{v.ultima_data ? ` · ${v.ultima_data}` : ''}</p>
    <p className="text-xs text-gray-700 mt-1">Acquisto {euro(v.prezzo_acquisto)} · Carta {euro(v.prezzo_vendita)}</p>
  </div>
);

const Doppione = ({ d, onDecidi, salvo }) => {
  const [testo, colore] = STATO[d.decisione] || ['Da decidere', 'bg-amber-100 text-amber-800'];
  const k = tid(d.chiave);
  return (
    <div className="border rounded-xl p-4 space-y-3 bg-stone-50/60" data-testid={`doppioni-item-${k}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-gray-600">{d.motivo}</p>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${colore}`} data-testid={`doppioni-stato-${k}`}>{testo}</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <VinoBox v={d.vino_a} etichetta="Vino A" />
        <VinoBox v={d.vino_b} etichetta="Vino B (nome che resta se unisci)" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={() => onDecidi(d, 'separati')} disabled={salvo || d.decisione === 'separati'}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border text-gray-700 hover:bg-white disabled:opacity-50" data-testid={`doppioni-separa-${k}`}>
          <Split className="h-4 w-4" /> Tieni separati
        </button>
        <button onClick={() => onDecidi(d, 'unisci')} disabled={salvo || d.decisione === 'unisci'}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50" data-testid={`doppioni-unisci-${k}`}>
          <GitMerge className="h-4 w-4" /> Unisci
        </button>
      </div>
    </div>
  );
};

export const DoppioniDialog = ({ open, onClose, getAuthHeaders, onCambiato }) => {
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  const carica = useCallback(async () => {
    setCaricando(true);
    try {
      const r = await axios.get(`${API}/api/vini-doppioni`, { headers: getAuthHeaders() });
      setDati(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile leggere i doppioni');
    } finally {
      setCaricando(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { if (open) carica(); }, [open, carica]);
  if (!open) return null;

  const decidi = async (d, decisione) => {
    setSalvo(true);
    try {
      await axios.post(`${API}/api/vini-doppioni/decidi`, { chiave: d.chiave, decisione }, { headers: getAuthHeaders() });
      toast.success(decisione === 'unisci' ? `Uniti: resta "${d.vino_b.nome}"` : 'Tenuti separati');
      await carica();
      onCambiato?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Decisione non salvata');
    } finally {
      setSalvo(false);
    }
  };

  const lista = dati?.doppioni || [];
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="doppioni-dialog">
      <div className="bg-white w-full sm:max-w-3xl max-h-[95vh] sm:rounded-2xl rounded-t-2xl flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 text-gray-900"><GitMerge className="h-5 w-5 text-amber-700" /> Possibili doppioni</h2>
            <p className="text-xs text-gray-500 mt-0.5">I doppioni certi (stesso produttore, etichetta e formato) sono già uniti. Questi sono dubbi: decidi tu. Le fatture non vengono mai cancellate e puoi cambiare idea quando vuoi.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="doppioni-chiudi"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="doppioni-lista">
          {caricando && !dati && <p className="py-10 text-center text-sm text-gray-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Controllo la carta…</p>}
          {dati && !lista.length && <p className="py-10 text-center text-sm text-emerald-700 flex items-center justify-center gap-2" data-testid="doppioni-vuoto"><CheckCircle2 className="h-5 w-5" /> Nessun possibile doppione da confermare.</p>}
          {lista.map(d => <Doppione key={d.chiave} d={d} onDecidi={decidi} salvo={salvo} />)}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500" data-testid="doppioni-da-decidere">{dati ? `${dati.da_decidere} da decidere` : ''}</span>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100" data-testid="doppioni-annulla">Chiudi</button>
        </div>
      </div>
    </div>
  );
};
